/**
 * FRITEKSTSØK PÅ TVERS AV OBJEKTENE (`GET /v1/search`, AI-ANALYSE §6 `search`).
 *
 * =================================================================================================
 * HVER TYPE MED SIN EGEN LISTES TILGANG. Hver type spør cella dens egen liste spør om — registeret
 * (`booking:list`, `artist:list` eller `concert:list`), Kontakter (`contact:list`), programmet
 * (`performance:list`), Oppgaver (`task:list`), Ressurser (`resource:list`), Dokumenter
 * (`document:list`), festivalene og årgangene (`festival:list`, `edition:list`), konsertene
 * (`concert:list`), byråene og agentene (`booking:list`), innboksen (`thread:list`), kalenderen
 * (`event:list`), kontraktene (`contract:read`), vaktene (`shift:list`), de frivillige
 * (`volunteer:list`), salene og spillestedene (`venue:list`) og arrangementene (`venue_night:list`)
 * — med listas omfang og modulene i `can()`. En type leseren ikke har cella for, står bare ikke i
 * svaret: søket sier aldri at noe finnes som lista ikke ville vist.
 *
 * Det er ikke et oppslag som velgerne i `suggest.ts` (ti rader, ingen markør): svaret er en side med
 * markør. Rekkefølgen er typene i den rekkefølgen `types` ga dem, så treffkvalitet (det som BEGYNNER
 * med teksten først) og navn innen hver type. Én markør bærer hele posisjonen.
 *
 * `editionId` snevrer typene som hører til en årgang (programposter, oppgaver, dokumenter og mapper i
 * årgangens sone, kalenderhendelser, vakter, frivillige, arrangementer). Resten påvirkes ikke.
 *
 * Organisasjonene leseren er medlem av, er ikke en type: søket leter i organisasjonen man står i
 * (RLS), og medlemskapene står i `GET /v1/me`.
 */
import { z } from 'zod';

import { MAX_CURSOR_LENGTH, page } from '../http/pagination.js';

/** Standardrekkefølgen, og dermed rekkefølgen i svaret når `types` er utelatt. */
export const SEARCH_OBJECT_TYPES = [
  'artist',
  'person',
  'performance',
  'festival',
  'venue',
  'edition',
  'concert',
  'agency',
  'agent',
  'company',
  'task',
  'resource',
  'document',
  'folder',
  'thread',
  'event',
  'contract',
  'shift',
  'volunteer',
  'venue_room',
  'venue_event',
  'venue_market_room',
  'venue_directory_room',
] as const;
export const searchObjectTypeSchema = z.enum(SEARCH_OBJECT_TYPES).meta({ id: 'SearchObjectType' });
export type SearchObjectType = z.infer<typeof searchObjectTypeSchema>;

export const MAX_SEARCH_PAGE = 50;

const splitTypes = (value: unknown): unknown => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value;
  const text = String(value).trim();
  return text.length === 0 ? undefined : text.split(',').map((part) => part.trim());
};

export const globalSearchQuerySchema = z
  .object({
    q: z.string().trim().min(1).max(120),
    /** Kommaliste. Utelatt: alle. Rekkefølgen er rekkefølgen i svaret. */
    types: z
      .preprocess(
        splitTypes,
        z
          .array(searchObjectTypeSchema)
          .min(1)
          .max(SEARCH_OBJECT_TYPES.length)
          .transform((types) => [...new Set(types)]),
      )
      .optional(),
    editionId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(MAX_SEARCH_PAGE).default(20),
    cursor: z.string().max(MAX_CURSOR_LENGTH).optional(),
  })
  .meta({ id: 'GlobalSearchQuery' });
export type GlobalSearchQuery = z.infer<typeof globalSearchQuerySchema>;

export const searchResultSchema = z
  .object({
    objectType: searchObjectTypeSchema,
    /**
     * Objektets id slik resten av API-et slår det opp: artistens i registeret, PERSONENS (ikke
     * kontaktradens — én person, ett sted), dokumentets (vedlegget i en mappe), og ellers radens
     * egen: `festival` og `venue` er en festival-rad, `venue_event` et arrangement
     * (`/venue-nights/:id`), `venue_market_room` en sal i markedet, `venue_directory_room` en sal i
     * plattformregisteret, `agent` en agent i et byrå, `volunteer` en påmelding.
     */
    objectId: z.uuid(),
    title: z.string(),
    /** Det som skiller to like navn: sjanger, tittel/firma, årgang, status, ressurstype, mappe … */
    subtitle: z.string().nullable(),
    /** Årgangen treffet hører til, når det hører til en. */
    editionId: z.uuid().nullable(),
    /**
     * Stien i nettsiden som åpner objektet (uten opprinnelse, med søkeparametere), som
     * `/festivals/<id>/editions/<id>/performances/<id>` eller `/booking?agency=<id>`. Null når
     * nettsiden ikke har noen side for akkurat dette objektet hos denne leseren.
     */
    path: z.string().nullable(),
  })
  .meta({ id: 'SearchResult' });
export type SearchResult = z.infer<typeof searchResultSchema>;

export const globalSearchResponseSchema = page(searchResultSchema, 'GlobalSearchResponse');
export type GlobalSearchResponse = z.infer<typeof globalSearchResponseSchema>;
